import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFetchData } from '../../../hooks/useFetchData';
import { generateDiscoveryReport, startRun } from '../../../services/dealGPT';
import type { CategoryProgress, ProjectRun, ProjectWithLastScanned } from '../../../Types/dealGPT';
import {
  DealGPTDiscoveryView,
  DealGPTQuestionStatus,
  ProjectRunStatus,
} from '../../../Types/enums';
import { downloadExcel } from '../../../util/excel';
import {
  projectRunProgressPathUrl,
  projectRunScopesPathUrl,
  projectRunStatusesPathUrl,
} from '../../../util/routesUrls';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';
import { useNotifications } from '../../Providers/NotificationsProvider';
import DiscoveryView from '../Views/DiscoveryView';
import { createDiscoveryExcelReport } from './excel.report';

export type DiscoveryProps = {
  project: ProjectWithLastScanned;
  refetchProject: () => Promise<void>;
  onQuestionsPreferencesChanged: (newRun: boolean, collections: string[]) => void;
  setSelectedRun: (run: ProjectRun | undefined) => void;
  projectRuns: ProjectRun[] | undefined;
  selectedRun?: ProjectRun;
};

const DEFAULT_POLL_INTERVAL = 20000;

const Discovery = ({
  project,
  refetchProject,
  onQuestionsPreferencesChanged,
  setSelectedRun,
  selectedRun,
  projectRuns,
}: DiscoveryProps): JSX.Element => {
  const [openQuestionPreferences, setOpenQuestionPreferences] = useState<boolean>(false);
  const [selectedScopeFilters, setSelectedScopeFilters] = useState<string[]>([]);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<DealGPTQuestionStatus[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoadingReport, setIsLoadingReport] = useState<boolean>(false);
  const [lastScanned, setLastScanned] = useState<string>('Scan in progress...');
  const [expandAllCategories, setExpandAllCategories] = useState<boolean>(false);
  const [shouldFetchRunProgress, setShouldFetchRunProgress] = useState<boolean>(false);
  const [discoveryView, setDiscoveryView] = useState<DealGPTDiscoveryView>(
    DealGPTDiscoveryView.ALL_QUESTIONS
  );
  const [isRunFetchCompleted, setIsRunFetchCompleted] = useState(false);
  const startTimeMarkRef = useRef(performance.mark('discovery-start'));
  const { refetchNotifications } = useNotifications();
  const prevStatusRef = useRef<string | undefined>(project.projectRunStatus);

  const {
    data: runScopes,
    startPolling: startPollingRunScopes,
    stopPolling: stopPollingRunScopes,
  } = useFetchData<string[]>({
    urlPath: projectRunScopesPathUrl(project.id, selectedRun?.id ?? ''),
    skip: !selectedRun,
  });

  const {
    data: statuses,
    startPolling: startPollingQuestionStatuses,
    stopPolling: stopPollingQuestionStatuses,
  } = useFetchData<DealGPTQuestionStatus[]>({
    urlPath: projectRunStatusesPathUrl(project.id, selectedRun?.id ?? ''),
    skip: !selectedRun,
  });

  const {
    data: runProgress,
    refetch: refetchRunProgress,
    loading: isLoadingRunProgress,
    startPolling: startPollingRunProgress,
    stopPolling: stopPollingRunProgress,
  } = useFetchData<CategoryProgress[]>({
    urlPath: projectRunProgressPathUrl(
      project.id,
      selectedRun?.id ?? '',
      selectedScopeFilters,
      discoveryView === DealGPTDiscoveryView.SITE_VISIT_PREP
        ? [
            DealGPTQuestionStatus.MORE_INFO_NEEDED,
            DealGPTQuestionStatus.NOT_FOUND,
            DealGPTQuestionStatus.SKIPPED,
          ]
        : selectedStatusFilters,
      searchQuery
    ),
    skip: !selectedRun,
  });

  const analytics = useAnalytics();

  const handleOpenQuestionPreferences = useCallback(() => {
    setOpenQuestionPreferences(true);
  }, []);

  const handleCloseQuestionPreferences = useCallback(() => {
    setOpenQuestionPreferences(false);
  }, []);

  const handleQuestionsPreferencesChanged = useCallback(
    (newRun: boolean, collections: string[]) => {
      analytics.event(
        AnalyticsEvent.WM_IA_D_RUN_PROJECT,
        {
          id: project.id,
          value: `Collections=${collections.toString()}`,
        },
        {
          projectId: project.id,
          selectedCollections: collections,
        }
      );
      onQuestionsPreferencesChanged(newRun, collections);
      setOpenQuestionPreferences(false);
      setTimeout(() => setShouldFetchRunProgress(true), 2000);
    },
    [project, analytics, onQuestionsPreferencesChanged]
  );

  const handleStartRun = useCallback(async () => {
    if (!selectedRun) return;

    await startRun(project.id, selectedRun.id);

    startPollingRunProgress(5000);
    startPollingQuestionStatuses(5000);
    startPollingRunScopes(5000);

    refetchProject();
    refetchNotifications();
    setShouldFetchRunProgress(true);
  }, [
    project,
    selectedRun,
    refetchProject,
    setShouldFetchRunProgress,
    refetchNotifications,
    startPollingRunProgress,
    startPollingQuestionStatuses,
    startPollingRunScopes,
  ]);

  const handleGenerateReport = useCallback(async () => {
    try {
      setIsLoadingReport(true);
      analytics.event(
        AnalyticsEvent.WM_IA_D_GENERATE_REPORT,
        { id: project.id },
        {
          projectId: project.id,
          selectedScopes: selectedScopeFilters,
          discoveryView,
          selectedStatuses: selectedStatusFilters,
        }
      );
      let reportStatuses = [];

      if (discoveryView === DealGPTDiscoveryView.SITE_VISIT_PREP) {
        reportStatuses = [
          DealGPTQuestionStatus.MORE_INFO_NEEDED,
          DealGPTQuestionStatus.NOT_FOUND,
          DealGPTQuestionStatus.SKIPPED,
        ];
      } else {
        reportStatuses = selectedStatusFilters.length ? selectedStatusFilters : statuses ?? [];
      }

      const response = await generateDiscoveryReport(
        project.id,
        discoveryView,
        selectedScopeFilters.length ? selectedScopeFilters : runScopes ?? [],
        reportStatuses,
        selectedRun?.id
      );

      if (response.length === 0) {
        enqueueSnackbar('No data found to generate report', { variant: 'error' });
        return;
      }

      const workbook = createDiscoveryExcelReport(response, discoveryView);
      downloadExcel(
        workbook,
        `Intellio®-Advantage-${project.name}-${new Date().toISOString()}.xlsx`
      );
    } catch (error) {
      enqueueSnackbar(`Failed to generate report: ${error}`, { variant: 'error' });
    } finally {
      setIsLoadingReport(false);
    }
  }, [
    discoveryView,
    project.id,
    project.name,
    runScopes,
    selectedScopeFilters,
    statuses,
    selectedStatusFilters,
    analytics,
    selectedRun?.id,
  ]);

  const handleClearScopeFilters = () => {
    setSelectedScopeFilters([]);
  };

  const getScope = (scope: string) => {
    return selectedScopeFilters.find((s: string) => s === scope);
  };

  const isScopeSelected = (scope: string) => {
    const selectedScope = getScope(scope);
    return !!selectedScope;
  };

  const toggleScopeFilters = (scope: string) => {
    let selected = false;
    if (selectedScopeFilters.find((s) => s === scope)) {
      setSelectedScopeFilters((prevFilters) =>
        prevFilters.filter((prevScope) => prevScope !== scope)
      );
    } else {
      setSelectedScopeFilters([...selectedScopeFilters, scope]);
      selected = true;
    }
    analytics.event(
      AnalyticsEvent.WM_IA_D_QUESTION_FILTER,
      {
        id: project.id,
        value: `${scope}=${selected}`,
      },
      {
        projectId: project.id,
        scope,
        scopeWasSelected: !selected,
        scopeNowSelected: selected,
      }
    );
  };

  const handleClearStatusFilters = () => {
    setSelectedStatusFilters([]);
  };

  const isStatusSelected = (status: DealGPTQuestionStatus) => {
    return selectedStatusFilters.includes(status);
  };

  const toggleStatusFilters = (status: DealGPTQuestionStatus) => {
    let selected = false;
    if (selectedStatusFilters.find((s) => s === status)) {
      setSelectedStatusFilters((prevFilters) =>
        prevFilters.filter((prevStatus) => prevStatus !== status)
      );
    } else {
      setSelectedStatusFilters([...selectedStatusFilters, status]);
      selected = true;
    }
    analytics.event(
      AnalyticsEvent.WM_IA_D_QUESTION_FILTER,
      {
        id: project.id,
        value: `${status}=${selected}`,
      },
      {
        projectId: project.id,
        status: status,
        statusWasSelected: !selected,
        statusNowSelected: selected,
      }
    );
  };

  const wrapSetSearchQuery = (query: string) => {
    setSearchQuery(query);
  };

  const toggleCollapseExpandAllCategories = () => {
    analytics.event(
      AnalyticsEvent.WM_IA_D_EXPAND_ALL_CATEGORIES,
      {
        id: project.id,
        value: (!expandAllCategories).toString(),
      },
      {
        projectId: project.id,
        allCategoriesWereExpanded: expandAllCategories,
        allCategoriesNowExpanded: !expandAllCategories,
      }
    );
    setExpandAllCategories(!expandAllCategories);
  };

  const aggregateProgressData = useMemo(() => {
    const aggregateInfo: CategoryProgress = {
      name: 'aggregate',
      quantity: 0,
      quantityAnswered: 0,
      quantityMoreInfoNeeded: 0,
      quantityNotFound: 0,
      quantitySkipped: 0,
      quantityPending: 0,
      scope: '',
      collectionId: '',
    };

    runProgress?.forEach((category) => {
      aggregateInfo.quantity += category.quantity;
      if (
        aggregateInfo.quantityAnswered !== undefined &&
        aggregateInfo.quantityMoreInfoNeeded !== undefined &&
        aggregateInfo.quantityNotFound !== undefined &&
        aggregateInfo.quantitySkipped !== undefined &&
        category.quantityAnswered !== undefined &&
        category.quantityMoreInfoNeeded !== undefined &&
        category.quantityNotFound !== undefined &&
        category.quantitySkipped !== undefined
      ) {
        aggregateInfo.quantityAnswered += category.quantityAnswered;
        aggregateInfo.quantityMoreInfoNeeded += category.quantityMoreInfoNeeded;
        aggregateInfo.quantityNotFound += category.quantityNotFound;
        aggregateInfo.quantitySkipped += category.quantitySkipped;
      }
    });

    return aggregateInfo;
  }, [runProgress]);

  // Manually refetch run progress data
  useEffect(() => {
    if (shouldFetchRunProgress) {
      refetchRunProgress();
      setShouldFetchRunProgress(false);
    }
  }, [shouldFetchRunProgress, refetchRunProgress]);

  // Is fist poll when there are no filters applied and no run progress data
  const isFirstPoll = useMemo(
    () =>
      searchQuery === '' &&
      selectedStatusFilters.length == 0 &&
      selectedScopeFilters.length === 0 &&
      (!runProgress || runProgress.length === 0),
    [searchQuery, selectedStatusFilters, selectedScopeFilters, runProgress]
  );
  // Starts polling for the project run progress, question statuses, and run scopes
  // 5s interval for the first poll, 20s for subsequent polls
  useEffect(() => {
    const pollInterval = isFirstPoll ? 5000 : DEFAULT_POLL_INTERVAL;
    if (
      project.projectRunStatus === ProjectRunStatus.PROCESSING_QUESTIONS ||
      project.projectRunStatus === ProjectRunStatus.INSERTING_QUESTIONS
    ) {
      startPollingRunProgress(pollInterval);
      startPollingQuestionStatuses(pollInterval);
      startPollingRunScopes(pollInterval);
    }

    return () => {
      stopPollingRunProgress();
      stopPollingQuestionStatuses();
      stopPollingRunScopes();
    };
  }, [
    isFirstPoll,
    project.projectRunStatus,
    startPollingRunProgress,
    stopPollingRunProgress,
    startPollingQuestionStatuses,
    stopPollingQuestionStatuses,
    startPollingRunScopes,
    stopPollingRunScopes,
  ]);

  useEffect(() => {
    if (project.lastScanned) {
      setLastScanned(new Date(project.lastScanned).toLocaleString());
    } else {
      setLastScanned('No scan yet.');
    }
  }, [project.id, project.lastScanned]);

  // Mark the end of the loading process for analytics
  useEffect(() => {
    if (runProgress && runProgress.length > 0) {
      setIsRunFetchCompleted(true);
    }
  }, [runProgress]);

  useEffect(() => {
    // Check if all Run/category data have loaded
    if (isRunFetchCompleted) {
      // Mark the end of the loading process
      const endTimeMark = performance.mark('discovery-end');

      // Calculate page load time
      const pageLoadTime = endTimeMark.startTime - startTimeMarkRef.current.startTime;

      //Send Google Analytic Event
      analytics.event(
        AnalyticsEvent.WM_IA_D_PAGE_LOAD_TIME,
        {
          id: project.id,
          value: pageLoadTime.toFixed(2),
        },
        {
          projectId: project.id,
          pageLoadTime: pageLoadTime.toFixed(2),
        }
      );

      // Clean up performance entries
      performance.clearMarks();
      performance.clearMeasures();
    }
  }, [isRunFetchCompleted, analytics, project.id]);

  useEffect(() => {
    const prev = prevStatusRef.current;
    const curr = project.projectRunStatus;

    const wasProcessing =
      prev === ProjectRunStatus.PROCESSING_QUESTIONS ||
      prev === ProjectRunStatus.INSERTING_QUESTIONS;

    const isNowDone = curr === ProjectRunStatus.COMPLETED_SUCCESSFULLY;

    if (wasProcessing && isNowDone) {
      // Force one last snapshot so the bars fill correctly
      refetchRunProgress();
    }

    prevStatusRef.current = curr;
  }, [project.projectRunStatus, refetchRunProgress]);

  return (
    <DiscoveryView
      project={project}
      runScopes={runScopes ?? []}
      statuses={statuses ?? []}
      selectedStatusFilters={selectedStatusFilters}
      searchQuery={searchQuery}
      setSearchQuery={wrapSetSearchQuery}
      isLoadingRunProgress={isLoadingRunProgress}
      selectedRun={selectedRun}
      isLoadingReport={isLoadingReport}
      openQuestionPreferences={openQuestionPreferences}
      lastScanned={lastScanned}
      setShouldFetchRunProgress={setShouldFetchRunProgress}
      projectRuns={projectRuns}
      runProgressData={runProgress ?? []}
      aggregateProgressData={aggregateProgressData}
      expandAllCategories={expandAllCategories}
      handleQuestionsPreferencesChanged={handleQuestionsPreferencesChanged}
      handleOpenQuestionPreferences={handleOpenQuestionPreferences}
      handleCloseQuestionPreferences={handleCloseQuestionPreferences}
      handleStartRun={handleStartRun}
      handleGenerateReport={handleGenerateReport}
      setSelectedRun={setSelectedRun}
      isScopeSelected={isScopeSelected}
      isStatusSelected={isStatusSelected}
      toggleScopeFilters={toggleScopeFilters}
      toggleStatusFilters={toggleStatusFilters}
      handleClearScopeFilters={handleClearScopeFilters}
      handleClearStatusFilters={handleClearStatusFilters}
      toggleCollapseExpandAllCategories={toggleCollapseExpandAllCategories}
      discoveryView={discoveryView}
      setDiscoveryView={setDiscoveryView}
    />
  );
};

export default Discovery;
